import { lazy, Suspense, type ComponentType } from "react";
import { Route, Switch } from "wouter";

import { routerPattern } from "@base/site/match";
import {
  activeRoutes,
  getRoute,
  routePaths,
  type RouteId,
} from "@base/site/routes";
import { ADMIN_PATH } from "@/lib/admin";

import { INCLUDE_DRAFTS } from "./env";
import { BlogSkeleton, PageFallback } from "./fallbacks";
import { RouteHead } from "./RouteHead";
import { useHashScroll } from "./useHashScroll";
import { ROUTE_COMPONENTS } from "./routeComponents";

// Admin pages stay outside the registry: their path comes from a Vite env var
// and they are never indexed or prerendered.
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminBlogList = lazy(() => import("@/pages/admin/blog/AdminBlogList"));
const AdminBlogEditor = lazy(
  () => import("@/pages/admin/blog/AdminBlogEditor")
);
const AdminBlogSettings = lazy(
  () => import("@/pages/admin/blog/AdminBlogSettings")
);

const ADMIN_ROUTES: [string, ComponentType][] = [
  [`/${ADMIN_PATH}/login`, AdminLogin],
  [`/${ADMIN_PATH}/blog`, AdminBlogList],
  [`/${ADMIN_PATH}/blog/settings`, AdminBlogSettings],
  [`/${ADMIN_PATH}/blog/new`, AdminBlogEditor],
  [`/${ADMIN_PATH}/blog/edit/:slug`, AdminBlogEditor],
];

function RoutePage({ id }: { id: RouteId }) {
  useHashScroll(id);
  const Page = ROUTE_COMPONENTS[id];
  const Fallback =
    getRoute(id).section === "blog" ? BlogSkeleton : PageFallback;
  return (
    <>
      <RouteHead id={id} />
      <Suspense fallback={<Fallback />}>
        <Page />
      </Suspense>
    </>
  );
}

const REGISTRY_ROUTES = activeRoutes(INCLUDE_DRAFTS).flatMap(route =>
  routePaths(route).map(path => ({
    pattern: routerPattern(path),
    id: route.id as RouteId,
  }))
);

export function AppRoutes() {
  return (
    <Switch>
      {ADMIN_ROUTES.map(([path, Page]) => (
        <Route key={path} path={path}>
          <Suspense fallback={<PageFallback />}>
            <Page />
          </Suspense>
        </Route>
      ))}
      {REGISTRY_ROUTES.map(({ pattern, id }) => (
        <Route key={pattern} path={pattern}>
          <RoutePage id={id} />
        </Route>
      ))}
      <Route>
        <RoutePage id="notFound" />
      </Route>
    </Switch>
  );
}
